const LOCAL = [
  {
    name: "Brands Farm Ravensbourne",
    url: "https://www.brandsfarm.com.au/",
    description:
      "Brands Farm features three unique accommodation options, each offering its own sense of privacy, character and comfort. Whether you are escaping for a romantic weekend, travelling with family, or staying nearby for a wedding celebration, each space has been thoughtfully designed to suit different group sizes and styles of stay while sharing the same quiet luxury and natural beauty.",
  },
  {
    name: "Cloud Lake Ravensbourne",
    url: "https://www.cloudlake.com/accommodation",
    description:
      "Nestled within a private valley beside Ravensbourne National Park, Cloud Lake offers a collection of unique retreats including The Boathouse, Mother of Ducks cottage and the romantic Romany Wood glamping experience. Surrounded by rainforest, gardens and a private lake, it is ideal for couples seeking a secluded escape or small groups wanting to reconnect with nature.",
  },
  {
    name: "Kenti's Boutique",
    url: "https://www.kentisplace.com.au/",
    description:
      "Set in the serenity of 40 acres of subtropical rainforest on the outskirts of Ravensbourne, Kenti's Place is a privately owned holiday property set at over 600 metres elevation in Queensland's High Country — on the edge of the Great Dividing Range, about 90 minutes from Brisbane and 45 minutes from Toowoomba. The property sleeps 10 guests across two spaces: The House and The Studio. Both have been thoughtfully renovated to a high standard, blending warm, considered interiors with the wild beauty of the surrounding rainforest.",
  },
  {
    name: "Ravensbourne Escape",
    url: "https://ravensbourneescape.com.au/",
    description:
      "Perched among the clouds and crisp mountain air, Ravensbourne Escape sits on the eastern escarpment of the Great Dividing Range. Ravensbourne Escape offers a wide range of self-contained, luxury studios, chalets and holiday homes. Within easy reach of national parks, local lakes and quaint country towns, Ravensbourne Escape is the perfect place to explore this beautiful region.",
  },
];

const HOTELS = [
  {
    name: "The Arbour Boutique",
    url: "https://arbourapartments.com.au/apartments/",
    description:
      "Encapsulating spectacular panoramic views of the city and Toowoomba range and cuts a striking yet elegant silhouette on the eastern skyline, the Arbour Boutique offers generously appointed one, two and three bedroom, fully self-contained apartments filled with comfort, understated luxury and old-world charm.",
  },
  {
    name: "Quest Toowoomba",
    url: "https://www.questapartments.com.au/properties/qld/toowoomba/quest-toowoomba",
    description:
      "Quest Toowoomba is nestled in the heart of Australia's second-largest inland city, with some rooms giving guests coveted views over Queens Park — one of the city's oldest and most treasured parks. Modern apartment-style accommodation in the heart of Toowoomba, offering spacious studios and serviced apartments ideal for wedding guests seeking convenience and comfort.",
    note: 'Please call the hotel directly and use discount code "The Dairy" for a special guest rate.',
  },
  {
    name: "Oaks Toowoomba",
    url: "https://www.oakshotels.com/en/oaks-toowoomba-hotel",
    description:
      "A contemporary hotel located in Toowoomba's city centre, featuring a range of accommodation options to suit your needs, including hotel rooms, 2 bedroom, and 3 bedroom configurations, all with private balconies. Located in the heart of Toowoomba, there is easy access to local dining, shopping and attractions.",
  },
  {
    name: "Potters Hotel Toowoomba",
    url: "https://pottershoteltoowoomba.com.au/",
    description:
      "Located a short stroll to the Toowoomba CBD, Potters Hotel is an ideal choice of accommodation in the Garden City. Explore the laneway cafes, diverse restaurants, theatres, boutique shops, and various leisure activities. Potters, situated on Margaret Street, is a stone's throw from the Grand Central Shopping Centre and Laurel Bank Park.",
  },
];

function Property({ name, url, description, note }) {
  return (
    <div className="space-y-3" data-testid={`accommodation-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block italic text-[#76232F] text-base md:text-xl leading-tight hover:underline underline-offset-4 decoration-1 decoration-[#76232F]/50 transition-all"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {name}
      </a>
      <p
        className="text-[15px] md:text-base leading-[1.7] text-[#2C1A1D]/85"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {description}
      </p>
      {note && (
        <p
          className="text-[13px] italic leading-[1.7] text-[#76232F]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {note}
        </p>
      )}
    </div>
  );
}

import { SCRIPT_IMG, ScriptImage } from "@/components/ScriptImage";

function SectionHeading({ children, href, imgSrc, imgAlt }) {
  // If imgSrc provided, render the user's PNG instead of CSS-rendered text
  const inner = imgSrc ? (
    <ScriptImage src={imgSrc} alt={imgAlt || children} />
  ) : (
    <h3
      className="italic text-[#76232F] text-4xl md:text-5xl leading-tight"
      style={{ fontFamily: "'Pinyon Script', cursive" }}
    >
      {children}
    </h3>
  );
  return (
    <div className="text-center pt-2">
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="inline-block">
          {inner}
        </a>
      ) : (
        inner
      )}
    </div>
  );
}

export default function AccommodationPanel() {
  return (
    <div
      data-testid="panel-block-accommodation"
      className="max-w-[680px] mx-auto px-2 md:px-0 pb-16"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      {/* Intro */}
      <p className="text-center text-[15px] md:text-base leading-[1.8] text-[#2C1A1D]/85 mb-14">
        We can&rsquo;t wait to celebrate with you! Since the wedding day is fast
        approaching, we recommend booking accommodation early as availability is
        limited. We&rsquo;ve put together a list of places to stay below to help
        you get started.
      </p>

      {/* Local */}
      <SectionHeading imgSrc={SCRIPT_IMG.localAccomm} imgAlt="Local Accommodation">
        Local Accommodation
      </SectionHeading>
      <div className="mt-10 space-y-12">
        {LOCAL.map((p) => (
          <Property key={p.name} {...p} />
        ))}
      </div>

      {/* Divider */}
      <div className="my-16 border-t border-[#76232F]/15" />

      {/* Hotels & Apartments */}
      <SectionHeading imgSrc={SCRIPT_IMG.hotels} imgAlt="Hotels and Apartments">
        Hotels and Apartments
      </SectionHeading>
      <div className="mt-10 space-y-12">
        {HOTELS.map((p) => (
          <Property key={p.name} {...p} />
        ))}
      </div>

      {/* Divider */}
      <div className="my-16 border-t border-[#76232F]/15" />

      {/* Airbnb */}
      <SectionHeading href="https://www.airbnb.com.au" imgSrc={SCRIPT_IMG.airbnb} imgAlt="Airbnb">
        Airbnb
      </SectionHeading>
      <div className="mt-10 text-center">
        <p className="text-[15px] md:text-base leading-[1.8] text-[#2C1A1D]/85">
          For guests opting for additional flexibility, a variety of Airbnb and
          holiday rental options are available throughout Ravensbourne and the
          surrounding Toowoomba region.
        </p>
      </div>
    </div>
  );
}
